import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Plus, Search } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyTeamPresetsState } from '@/components/features/teams/EmptyTeamPresetsState';
import { TeamPresetCard } from '@/components/features/teams/TeamPresetCard';
import { TeamPresetCardSkeleton } from '@/components/features/teams/TeamPresetCardSkeleton';
import { TeamPresetFormSheet } from '@/components/features/teams/TeamPresetFormSheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { deleteTeamPreset } from '@/db/team-presets';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { useInitialSync } from '@/hooks/useInitialSync';
import { usePlayers } from '@/hooks/usePlayers';
import { useRouteAnimation } from '@/hooks/useRouteAnimation';
import { useTeamPresets } from '@/hooks/useTeamPresets';
import { handleUserFacingError } from '@/lib/error-handler';
import { slideUp, staggerConfig } from '@/lib/motion-config';
import { toastError } from '@/lib/toast-helpers';
import type { TeamPreset } from '@/schemas';

export const Route = createFileRoute('/team-presets/')({
  component: TeamPresetsPage,
});

function TeamPresetsPage() {
  const navigate = useNavigate();
  const { t } = useTranslation('common');
  const { isSyncing } = useInitialSync();
  const {
    teamPresets: presets,
    isLoading: isLoadingPresets,
    hasData: hasPresetData,
  } = useTeamPresets();
  const { players } = usePlayers();
  const [searchQuery, setSearchQuery] = useState('');
  const [editingPreset, setEditingPreset] = useState<TeamPreset | undefined>(undefined);
  const [showEditSheet, setShowEditSheet] = useState(false);

  useDocumentTitle(`${t('teams.title')} - ${t('app.name', 'One Score')}`);

  const showInitialSkeleton = (isSyncing || isLoadingPresets) && !hasPresetData;

  // Search functionality
  const filteredPresets = useMemo(() => {
    if (!searchQuery.trim()) {
      return presets.toSorted((a, b) => (a.name || '').localeCompare(b.name || ''));
    }

    const normalizedQuery = searchQuery.toLowerCase().trim();
    return presets
      .filter((preset) => {
        const nameMatch = preset.name?.toLowerCase().includes(normalizedQuery);
        const nicknameMatch = preset.nickname?.toLowerCase().includes(normalizedQuery);
        return nameMatch || nicknameMatch;
      })
      .toSorted((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [searchQuery, presets]);

  const handleCreateClick = () => {
    void navigate({ to: '/team-presets/new' });
  };

  const handleCardClick = (preset: TeamPreset) => {
    void navigate({
      to: '/team-presets/$presetId',
      params: { presetId: String(preset.id) },
    });
  };

  const handleEditForm = useCallback((preset: TeamPreset) => {
    setEditingPreset(preset);
    setShowEditSheet(true);
  }, []);

  const handleEditSuccess = useCallback(() => {
    setShowEditSheet(false);
    setEditingPreset(undefined);
  }, []);

  const handleDelete = async (preset: TeamPreset): Promise<void> => {
    try {
      await deleteTeamPreset(preset.id);
    } catch (error) {
      const errorMessage = handleUserFacingError(
        error,
        t('teams.deleteError', 'Failed to delete team preset'),
        { operation: 'deleteTeamPreset', presetId: preset.id },
      );
      toastError(errorMessage);
      throw error;
    }
  };

  const { shouldAnimate, routeKey } = useRouteAnimation();

  return (
    <div className="w-full max-w-6xl space-y-6 px-4 mx-auto">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t('teams.title')}</h1>
          <p className="text-muted-foreground">{t('teams.description')}</p>
        </div>
        {!showInitialSkeleton && presets.length > 0 && (
          <div className="w-full sm:w-auto">
            <Button className="w-full sm:w-auto" onClick={handleCreateClick}>
              <Plus className="mr-2 h-4 w-4" />
              {t('teams.createButton')}
            </Button>
          </div>
        )}
      </div>

      {/* Search */}
      {!showInitialSkeleton && presets.length >= 6 && (
        <div className="relative w-full sm:flex-1 sm:max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder={
              !showInitialSkeleton && presets.length > 0
                ? (() => {
                    const hasActiveFilters = searchQuery.trim().length > 0;
                    if (hasActiveFilters) {
                      return t('teams.searchWithCountFiltered', {
                        count: filteredPresets.length,
                        filtered: filteredPresets.length,
                        total: presets.length,
                      });
                    }
                    return t('teams.searchWithCount', { count: presets.length });
                  })()
                : t('teams.search')
            }
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
      )}

      {/* Loading State */}
      {showInitialSkeleton && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <TeamPresetCardSkeleton key={i} />
          ))}
        </div>
      )}

      {/* Empty State */}
      {!showInitialSkeleton && presets.length === 0 && (
        <EmptyTeamPresetsState onCreateClick={handleCreateClick} />
      )}

      {/* Presets Grid */}
      {!showInitialSkeleton && filteredPresets.length > 0 && (
        <motion.div
          key={`team-presets-grid-${routeKey}`}
          className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3"
          variants={slideUp}
          initial={shouldAnimate ? 'initial' : false}
          animate="animate"
          transition={staggerConfig.container}
        >
          <AnimatePresence mode="popLayout">
            {filteredPresets.map((preset) => (
              <motion.div
                key={preset.id}
                layout
                layoutId={`team-preset-card-${preset.id}`}
                variants={slideUp}
                initial="initial"
                animate="animate"
                exit="exit"
              >
                <TeamPresetCard
                  preset={preset}
                  onEdit={handleCardClick}
                  onEditForm={handleEditForm}
                  onDelete={handleDelete}
                  players={players}
                />
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
      )}

      {/* No Results from Search */}
      {!showInitialSkeleton &&
        presets.length > 0 &&
        filteredPresets.length === 0 &&
        searchQuery && (
          <div className="flex min-h-[200px] items-center justify-center">
            <p className="text-muted-foreground">{t('sessions.noResults')}</p>
          </div>
        )}

      {/* Edit Form Sheet */}
      {editingPreset && (
        <TeamPresetFormSheet
          open={showEditSheet}
          onOpenChange={setShowEditSheet}
          teamPreset={editingPreset}
          onSuccess={handleEditSuccess}
        />
      )}
    </div>
  );
}
